import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { Button, Card, Empty, Select, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { StatusCell } from '../../ui';
import { getApplicationNumberSearchLines } from '../../utils/applicationDisplayFormat';
import type { CompletionProjection, WorkspaceApplication, WorkspaceRoute, WorkspaceTask } from '../../store/api/workspaceApi';
import {
  WORKSPACE_GRID_CELL,
  formatWorkspaceTasksCount,
  formatWorkspaceCarrierAgreementLine,
  workspaceApplicationNumberLines,
  workspaceClientApplicationLines,
  WorkspaceTwoLineTableCell,
} from './WorkspaceTasks';
import {
  WORKSPACE_APPLICATION_REGISTRY_GRID_ROW,
  WORKSPACE_ROUTE_REGISTRY_GRID_ROW,
  WORKSPACE_ROUTE_REGISTRY_HEADER_ROW,
} from './workspaceGrid';
import { aggregateApplicationPaymentStatus } from './workspaceApplicationPaymentStatusProjection';
import {
  RouteApplicationsCompactText,
  RouteClientsCompactText,
} from '../../features/routes/RouteMultiAppCompactCells';
import {
  formatRouteApplicationsCompact,
  formatRouteClientsCompact,
  type RouteApplicationJoinRow,
} from '../../features/routes/routeMultiApplicationDisplay';
import {
  summarizeStopKindDates,
  workspaceRouteSortTimestamp,
  type CompactStopDatesSummary,
} from './workspaceRouteRegistryProjection';
import { WorkspaceRouteStopDatesCell } from './WorkspaceRouteStopDatesCell';
import { ExecutionCell } from '../../ui/cells';
import {
  WORKSPACE_APPLICATION_REGISTRY_HEADER_ROW,
  WorkspaceHeaderCell,
  WorkspaceListToolbar,
  workspaceHighlightForApplication,
  workspaceHighlightForRoute,
  workspaceRowHighlightStyle,
  matchesApplicationStatusFilter,
  matchesRouteStatusFilter,
  toggleSort,
  type WorkspaceHasTasksFilter,
  type WorkspaceScopeFilter,
  type WorkspaceSortKey,
  type WorkspaceStatusFilter,
} from './workspaceListShared';
import { applicationCardPath, dashboardRoleKind, routeCardPath } from '../../utils/roleAwareEntityNavigation';
import {
  resolveWorkspaceRegistryListLayout,
  type WorkspaceRegistryVariant,
} from './workspaceRegistryLayout';

const { Text } = Typography;

function normalize(s: string | null | undefined): string {
  return String(s || '').trim().toLowerCase();
}

export interface WorkspaceMyRoutesProps {
  routes: WorkspaceRoute[];
  taskCounts?: Record<number, number>;
  allActiveTasks?: WorkspaceTask[];
  onInlineTaskAction?: (task: WorkspaceTask) => void;
  /** Операционный финансовый контекст (бухгалтер / руководство) вместо перехода в карточку. */
  onOpenFinanceContext?: (p: { kind: 'route' | 'application'; id: number }) => void;
  /** embedded — фиксированная высота; drawer — flex-fill внутри Drawer. */
  variant?: WorkspaceRegistryVariant;
}

type UnifiedRouteRow = {
  id: number;
  status: string;
  routeLabel: string;
  applications: RouteApplicationJoinRow[];
  fallbackClient: WorkspaceRoute['client'];
  carrierName: string | null;
  carrierContractRequestNumber: string | null;
  carrierContractRequestDate: string | null;
  carrierAgreement: WorkspaceRoute['carrierAgreement'];
  isCompanyRoute: boolean;
  loadingDates: CompactStopDatesSummary;
  unloadingDates: CompactStopDatesSummary;
  sortDate: number;
  taskCount: number;
  financeClosed: boolean;
  completionProjection: CompletionProjection | null;
  searchBlob: string;
};

function workspaceRouteApplicationRows(route: WorkspaceRoute): RouteApplicationJoinRow[] {
  return (route.applications ?? []).map((link) => ({
    applicationId: link.applicationId,
    application: link.application ?? null,
  }));
}

type CompletionViewFilter = 'all' | 'terminal' | 'fully_closed' | 'requires_closure' | 'active';

function normalizedStatus(status: string | null | undefined): string {
  return String(status || '').trim().toUpperCase();
}

function isRequiresClosure(projection: CompletionProjection | null | undefined): boolean {
  return Boolean(projection?.physicalComplete && !projection?.overallComplete);
}

function isAttentionNeeded(
  projection: CompletionProjection | null | undefined,
  overdue: boolean,
  taskCount: number
): boolean {
  if (overdue) return true;
  if (isRequiresClosure(projection)) return true;
  if (!projection) return taskCount > 0;
  if (!projection.operationalComplete || !projection.financialComplete || !projection.workflowComplete) return true;
  return taskCount > 0;
}

function completionPriorityRank(
  projection: CompletionProjection | null | undefined,
  overdue: boolean,
  taskCount: number
): number {
  if (overdue) return 1;
  if (isRequiresClosure(projection)) return 2;
  if (isAttentionNeeded(projection, overdue, taskCount)) return 3;
  if (projection?.overallComplete) return 5;
  return 4;
}

export const WorkspaceMyRoutes: React.FC<WorkspaceMyRoutesProps> = ({
  routes,
  taskCounts = {},
  allActiveTasks = [],
  onInlineTaskAction,
  onOpenFinanceContext,
  variant = 'embedded',
}) => {
  const listLayout = useMemo(() => resolveWorkspaceRegistryListLayout(variant), [variant]);
  const navigate = useNavigate();
  const roleKind = useSelector((s: RootState) => dashboardRoleKind(s.auth.user?.role?.name));
  const [hoveredRouteId, setHoveredRouteId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<WorkspaceStatusFilter>('all');
  const [scope, setScope] = useState<WorkspaceScopeFilter>('all');
  const [hasTasksFilter, setHasTasksFilter] = useState<WorkspaceHasTasksFilter>('all');
  const [completionView, setCompletionView] = useState<CompletionViewFilter>('all');
  const [sortKey, setSortKey] = useState<WorkspaceSortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    const raw = localStorage.getItem('workspace_my_routes_filters_v1');
    if (!raw) return;
    try {
      const saved = JSON.parse(raw) as Partial<{
        search: string;
        statusFilter: WorkspaceStatusFilter;
        scope: WorkspaceScopeFilter;
        hasTasksFilter: WorkspaceHasTasksFilter;
        completionView: CompletionViewFilter;
      }>;
      if (saved.search != null) setSearch(saved.search);
      if (saved.statusFilter) setStatusFilter(saved.statusFilter);
      if (saved.scope) setScope(saved.scope);
      if (saved.hasTasksFilter) setHasTasksFilter(saved.hasTasksFilter);
      if (saved.completionView) setCompletionView(saved.completionView);
    } catch {
      // ignore malformed saved state
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      'workspace_my_routes_filters_v1',
      JSON.stringify({ search, statusFilter, scope, hasTasksFilter, completionView })
    );
  }, [search, statusFilter, scope, hasTasksFilter, completionView]);

  const mineRouteIds = useMemo(() => {
    const s = new Set<number>();
    allActiveTasks.forEach((t) => {
      if (t.routeId != null) s.add(t.routeId);
    });
    return s;
  }, [allActiveTasks]);

  const baseRows: UnifiedRouteRow[] = useMemo(() => {
    return routes.map((route) => {
      const applicationRows = workspaceRouteApplicationRows(route);
      const routeLabel = route.number?.trim() ? route.number : `#${route.id}`;
      const appsSummary = formatRouteApplicationsCompact(applicationRows);
      const clientsSummary = formatRouteClientsCompact(applicationRows, route.client ?? null);
      const loadingDates = summarizeStopKindDates(route.stops, 'loading', {
        planned: route.plannedLoadingDate,
        actual: route.actualLoadingDate,
      });
      const unloadingDates = summarizeStopKindDates(route.stops, 'unloading', {
        planned: route.plannedUnloadingDate,
        actual: route.actualUnloadingDate,
      });
      const sortDate = workspaceRouteSortTimestamp(route);
      const carrier = route.carrier?.name || '';
      const completionProjection = route.completionProjection ?? null;
      const financeClosed =
        completionProjection != null
          ? Boolean(completionProjection.financialComplete)
          : (route as { aggregatedStatus?: { overallStatus?: string } }).aggregatedStatus?.overallStatus
            ? (route as { aggregatedStatus?: { overallStatus?: string } }).aggregatedStatus?.overallStatus === 'OK'
            : true;
      const searchBlob = [
        routeLabel,
        String(route.id),
        route.number,
        route.carrierContractRequestNumber,
        appsSummary.display,
        ...appsSummary.tooltipLines,
        clientsSummary.display,
        ...clientsSummary.tooltipLines,
        carrier,
        loadingDates.planLine,
        loadingDates.factLine,
        unloadingDates.planLine,
        unloadingDates.factLine,
        route.loadingCity,
        route.unloadingCity,
        route.direction,
      ]
        .map((x) => normalize(x))
        .join(' ');

      return {
        id: route.id,
        status: route.status || '',
        routeLabel,
        applications: applicationRows,
        fallbackClient: route.client ?? null,
        carrierName: route.carrier?.name || null,
        carrierContractRequestNumber: route.carrierContractRequestNumber ?? null,
        carrierContractRequestDate: route.carrierContractRequestDate ?? null,
        carrierAgreement: route.carrierAgreement ?? null,
        isCompanyRoute: Boolean((route as { isCompanyRoute?: boolean }).isCompanyRoute),
        loadingDates,
        unloadingDates,
        sortDate,
        taskCount: taskCounts[route.id] ?? 0,
        financeClosed,
        completionProjection,
        searchBlob,
      };
    });
  }, [routes, taskCounts]);

  const filteredSorted = useMemo(() => {
    const q = normalize(search);
    let list = baseRows.filter((row) => {
      if (q && !row.searchBlob.includes(q)) return false;
      if (completionView === 'terminal' && !Boolean(row.completionProjection?.physicalComplete)) return false;
      if (completionView === 'fully_closed' && !Boolean(row.completionProjection?.overallComplete)) return false;
      if (completionView === 'requires_closure' && !isRequiresClosure(row.completionProjection)) return false;
      if (completionView === 'active' && Boolean(row.completionProjection?.overallComplete)) return false;
      const { overdue } = workspaceHighlightForRoute(row.id, allActiveTasks);
      if (!matchesRouteStatusFilter(row.status, statusFilter, overdue, row.completionProjection)) return false;
      if (scope === 'mine' && !mineRouteIds.has(row.id)) return false;
      if (hasTasksFilter === 'with' && row.taskCount <= 0) return false;
      if (hasTasksFilter === 'without' && row.taskCount > 0) return false;
      return true;
    });

    const effectiveKey = sortKey ?? 'date';
    const effectiveDir = sortKey == null ? 'desc' : sortDir;

    list = [...list].sort((a, b) => {
      const aOverdue = workspaceHighlightForRoute(a.id, allActiveTasks).overdue;
      const bOverdue = workspaceHighlightForRoute(b.id, allActiveTasks).overdue;
      const priorityA = completionPriorityRank(a.completionProjection, aOverdue, a.taskCount);
      const priorityB = completionPriorityRank(b.completionProjection, bOverdue, b.taskCount);
      if (priorityA !== priorityB) return priorityA - priorityB;
      if (effectiveKey === 'date') {
        const d = a.sortDate - b.sortDate;
        if (d !== 0) return effectiveDir === 'asc' ? d : -d;
        return b.id - a.id;
      }
      if (effectiveKey === 'tasks') {
        const d = a.taskCount - b.taskCount;
        if (d !== 0) return effectiveDir === 'asc' ? d : -d;
        return b.id - a.id;
      }
      const c = String(a.status || '').localeCompare(String(b.status || ''), 'ru');
      if (c !== 0) return effectiveDir === 'asc' ? c : -c;
      const d = a.sortDate - b.sortDate;
      if (d !== 0) return effectiveDir === 'asc' ? d : -d;
      return b.id - a.id;
    });

    return list;
  }, [
    baseRows,
    search,
    statusFilter,
    scope,
    hasTasksFilter,
    completionView,
    sortKey,
    sortDir,
    allActiveTasks,
    mineRouteIds,
  ]);

  const onHeaderSort = (key: WorkspaceSortKey) => {
    const next = toggleSort(key, sortKey, sortDir);
    setSortKey(next.key);
    setSortDir(next.dir);
  };

  const activeFilters = useMemo(() => {
    const items: string[] = [];
    if (search.trim()) items.push('Поиск');
    if (statusFilter === 'overdue') items.push('Просрочки');
    else if (statusFilter === 'in_work') items.push('В работе');
    else if (statusFilter === 'completed') items.push('Завершенные');
    if (scope === 'mine') items.push('Мои');
    if (hasTasksFilter === 'with') items.push('С задачами');
    if (hasTasksFilter === 'without') items.push('Без задач');
    if (completionView === 'terminal') items.push('Только физически завершенные');
    if (completionView === 'fully_closed') items.push('Только полностью закрытые');
    if (completionView === 'requires_closure') items.push('Требуют закрытия');
    if (completionView === 'active') items.push('Только активные');
    return items;
  }, [search, statusFilter, scope, hasTasksFilter, completionView]);

  const handleQuickFilter = (preset: 'problems' | 'overdue' | 'active' | 'without_tasks' | 'awaiting_docs') => {
    if (preset === 'problems') {
      setStatusFilter('overdue');
      setHasTasksFilter('with');
      return;
    }
    if (preset === 'overdue') {
      setStatusFilter('overdue');
      return;
    }
    if (preset === 'active') {
      setScope('mine');
      setStatusFilter('in_work');
      setHasTasksFilter('with');
      return;
    }
    if (preset === 'without_tasks') {
      setHasTasksFilter('without');
      setStatusFilter('all');
      return;
    }
    setSearch('документ');
    setStatusFilter('in_work');
    setHasTasksFilter('with');
  };

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setScope('all');
    setHasTasksFilter('all');
    setCompletionView('all');
  };

  const openRouteDetails = (id: number) => {
    const preferDrawer =
      (roleKind === 'accountant' || roleKind === 'management') && typeof onOpenFinanceContext === 'function';
    if (preferDrawer) {
      onOpenFinanceContext!({ kind: 'route', id });
      return;
    }
    navigate(routeCardPath(id, roleKind));
  };
  const firstInlineTaskForRoute = (routeId: number): WorkspaceTask | null => {
    const candidates = allActiveTasks.filter((task) => task.routeId === routeId);
    if (!candidates.length) return null;
    const sorted = [...candidates].sort((a, b) => {
      const aOverdue = String(a.status || '').toUpperCase() === 'OVERDUE' ? 1 : 0;
      const bOverdue = String(b.status || '').toUpperCase() === 'OVERDUE' ? 1 : 0;
      if (aOverdue !== bOverdue) return bOverdue - aOverdue;
      const aDue = a.dueDate ? dayjs(a.dueDate).valueOf() : Number.POSITIVE_INFINITY;
      const bDue = b.dueDate ? dayjs(b.dueDate).valueOf() : Number.POSITIVE_INFINITY;
      if (aDue !== bDue) return aDue - bDue;
      return a.id - b.id;
    });
    return sorted[0] ?? null;
  };
  const attentionRoutes = useMemo(
    () =>
      filteredSorted.filter((row) => {
        const overdue = workspaceHighlightForRoute(row.id, allActiveTasks).overdue;
        return isAttentionNeeded(row.completionProjection, overdue, row.taskCount);
      }).slice(0, 5),
    [filteredSorted, allActiveTasks]
  );

  if (!routes.length) {
    return (
      <Card
        title={listLayout.hideCardChrome ? undefined : 'Маршруты'}
        bordered={!listLayout.hideCardChrome}
        style={listLayout.hideCardChrome ? listLayout.cardStyle : undefined}
      >
        <Text type="secondary">Маршрутов нет.</Text>
      </Card>
    );
  }

  return (
    <Card
      title={listLayout.hideCardChrome ? undefined : 'Все маршруты'}
      bordered={!listLayout.hideCardChrome}
      style={listLayout.cardStyle}
      styles={{
        body: listLayout.cardBodyStyle,
      }}
    >
      <WorkspaceListToolbar
        searchPlaceholder="Маршрут, заявка, клиент, перевозчик, даты…"
        search={search}
        onSearch={setSearch}
        status={statusFilter}
        onStatus={setStatusFilter}
        scope={scope}
        onScope={setScope}
        hasTasks={hasTasksFilter}
        onHasTasks={setHasTasksFilter}
        onQuickFilter={handleQuickFilter}
        activeFilters={activeFilters}
        onResetFilters={resetFilters}
      />
      <div style={{ marginBottom: 8 }}>
        <Select
          size="small"
          value={completionView}
          onChange={(v) => setCompletionView(v)}
          style={{ minWidth: 260 }}
          options={[
            { value: 'all', label: 'Все маршруты' },
            { value: 'terminal', label: 'Только физически завершенные' },
            { value: 'fully_closed', label: 'Только полностью закрытые' },
            { value: 'requires_closure', label: 'Требуют закрытия' },
            { value: 'active', label: 'Только активные' },
          ]}
        />
      </div>
      {attentionRoutes.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary">Требуют внимания:</Text>{' '}
          <Space wrap size={6}>
            {attentionRoutes.map((row) => {
              const overdue = workspaceHighlightForRoute(row.id, allActiveTasks).overdue;
              const label = overdue
                ? 'Просрочка/блокировка'
                : isRequiresClosure(row.completionProjection)
                  ? 'Требует закрытия'
                  : 'Требует внимания';
              return (
                <Tag key={`attn_route_${row.id}`} color={overdue ? 'red' : 'orange'}>
                  {row.routeLabel}: {label}
                </Tag>
              );
            })}
          </Space>
        </div>
      )}
      <div style={listLayout.scrollAreaStyle}>
        <div style={{ ...WORKSPACE_ROUTE_REGISTRY_HEADER_ROW, flexShrink: 0 }}>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Маршрут
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Заявки
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Клиенты
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Перевозчик
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Договор-заявка
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey="date" activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Погрузка
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Выгрузка
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey="tasks" activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Задачи
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey="status" activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Статус
          </WorkspaceHeaderCell>
        </div>
        {!filteredSorted.length ? (
          <Empty description="Нет строк по фильтрам" style={{ margin: '24px 0' }} />
        ) : (
          filteredSorted.map((route) => {
            const highlight = workspaceHighlightForRoute(route.id, allActiveTasks);
            const rowStyle = workspaceRowHighlightStyle(highlight.overdue, highlight.today);

            return (
              <div
                key={route.id}
                role="presentation"
                onClick={() => openRouteDetails(route.id)}
                onMouseEnter={() => setHoveredRouteId(route.id)}
                onMouseLeave={() => setHoveredRouteId(null)}
                style={{
                  ...rowStyle,
                  ...(hoveredRouteId === route.id ? { background: '#f5f5f5' } : {}),
                  borderRadius: 8,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <div style={{ ...WORKSPACE_ROUTE_REGISTRY_GRID_ROW, borderBottom: 'none', background: 'transparent' }}>
                  <Text strong style={WORKSPACE_GRID_CELL}>
                    {route.routeLabel}
                  </Text>
                  <div style={WORKSPACE_GRID_CELL} onClick={(e) => e.stopPropagation()}>
                    <RouteApplicationsCompactText applications={route.applications} />
                  </div>
                  <div style={WORKSPACE_GRID_CELL} onClick={(e) => e.stopPropagation()}>
                    <RouteClientsCompactText
                      applications={route.applications}
                      fallbackClient={route.fallbackClient}
                      truncate={28}
                    />
                  </div>
                  <Text type="secondary" style={WORKSPACE_GRID_CELL} title={route.carrierName ?? undefined}>
                    {route.carrierName ?? '—'}
                  </Text>
                  <div style={WORKSPACE_GRID_CELL}>
                    <ExecutionCell
                      route={{
                        carrierContractRequestNumber: route.carrierContractRequestNumber,
                        carrierContractRequestDate: route.carrierContractRequestDate,
                        carrierAgreement: route.carrierAgreement,
                        isCompanyRoute: route.isCompanyRoute,
                      }}
                    />
                  </div>
                  <div style={WORKSPACE_GRID_CELL}>
                    <WorkspaceRouteStopDatesCell summary={route.loadingDates} />
                  </div>
                  <div style={WORKSPACE_GRID_CELL}>
                    <WorkspaceRouteStopDatesCell summary={route.unloadingDates} />
                  </div>
                  <div style={{ ...WORKSPACE_GRID_CELL, display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Text>{formatWorkspaceTasksCount(route.taskCount)}</Text>
                    {route.taskCount > 0 && onInlineTaskAction && (
                      <Button
                        size="small"
                        type="link"
                        style={{ paddingInline: 0 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const task = firstInlineTaskForRoute(route.id);
                          if (task) onInlineTaskAction(task);
                        }}
                      >
                        Выполнить
                      </Button>
                    )}
                    {route.completionProjection?.physicalComplete && (
                      <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                        Перевозка завершена
                      </Tag>
                    )}
                    {route.completionProjection?.physicalComplete && !route.completionProjection?.overallComplete && (
                      <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                        Есть незакрытые этапы
                      </Tag>
                    )}
                    {route.completionProjection?.overallComplete && (
                      <Tag color="green" style={{ marginInlineEnd: 0 }}>
                        Полностью закрыт
                      </Tag>
                    )}
                  </div>
                  <span
                    style={{
                      ...WORKSPACE_GRID_CELL,
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <StatusCell status={route.status} />
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
};

export interface WorkspaceMyApplicationsProps {
  applications: WorkspaceApplication[];
  taskCounts?: Record<number, number>;
  allActiveTasks?: WorkspaceTask[];
  onInlineTaskAction?: (task: WorkspaceTask) => void;
  onOpenFinanceContext?: (p: { kind: 'route' | 'application'; id: number }) => void;
  variant?: WorkspaceRegistryVariant;
}

type UnifiedAppRow = {
  id: number;
  status: string;
  direction: string;
  internalLine1: string;
  internalLine2: string;
  clientLine1: string;
  clientLine2: string;
  sortDate: number;
  clientName: string;
  contractLine: string;
  taskCount: number;
  paymentStatus: string;
  financeClosed: boolean;
  cargoDelivered: boolean;
  completionProjection: CompletionProjection | null;
  searchBlob: string;
};

export const WorkspaceMyApplications: React.FC<WorkspaceMyApplicationsProps> = ({
  applications,
  taskCounts = {},
  allActiveTasks = [],
  onInlineTaskAction,
  onOpenFinanceContext,
  variant = 'embedded',
}) => {
  const listLayout = useMemo(() => resolveWorkspaceRegistryListLayout(variant), [variant]);
  const navigate = useNavigate();
  const roleKind = useSelector((s: RootState) => dashboardRoleKind(s.auth.user?.role?.name));
  const [hoveredApplicationId, setHoveredApplicationId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<WorkspaceStatusFilter>('all');
  const [scope, setScope] = useState<WorkspaceScopeFilter>('all');
  const [hasTasksFilter, setHasTasksFilter] = useState<WorkspaceHasTasksFilter>('all');
  const [completionView, setCompletionView] = useState<CompletionViewFilter>('all');
  const [sortKey, setSortKey] = useState<WorkspaceSortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    const raw = localStorage.getItem('workspace_my_applications_filters_v1');
    if (!raw) return;
    try {
      const saved = JSON.parse(raw) as Partial<{
        search: string;
        statusFilter: WorkspaceStatusFilter;
        scope: WorkspaceScopeFilter;
        hasTasksFilter: WorkspaceHasTasksFilter;
        completionView: CompletionViewFilter;
      }>;
      if (saved.search != null) setSearch(saved.search);
      if (saved.statusFilter) setStatusFilter(saved.statusFilter);
      if (saved.scope) setScope(saved.scope);
      if (saved.hasTasksFilter) setHasTasksFilter(saved.hasTasksFilter);
      if (saved.completionView) setCompletionView(saved.completionView);
    } catch {
      // ignore malformed saved state
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      'workspace_my_applications_filters_v1',
      JSON.stringify({ search, statusFilter, scope, hasTasksFilter, completionView })
    );
  }, [search, statusFilter, scope, hasTasksFilter, completionView]);

  const mineApplicationIds = useMemo(() => {
    const s = new Set<number>();
    allActiveTasks.forEach((t) => {
      if (t.applicationId != null) s.add(t.applicationId);
    });
    return s;
  }, [allActiveTasks]);

  const baseRows: UnifiedAppRow[] = useMemo(() => {
    return applications.map((app) => {
      const clientName = app.client?.name?.trim() || app.client?.shortName?.trim() || '';
      const direction = app.direction || '—';
      const internalRaw =
        (app.internalNumber && String(app.internalNumber).trim()) ||
        (app.number && String(app.number).trim()) ||
        String(app.id);
      const internalLines = workspaceApplicationNumberLines(internalRaw, app.applicationDate ?? null);
      const clientLines = workspaceClientApplicationLines(app.externalNumber ?? null, app.basisDate ?? null);
      const sortDate =
        app.createdAt != null && String(app.createdAt).trim() !== ''
          ? dayjs(app.createdAt).valueOf()
          : app.applicationDate
            ? dayjs(app.applicationDate).valueOf()
            : 0;
      const contractLine = formatWorkspaceCarrierAgreementLine(app.contract ?? null);
      const numberFields = { internalNumber: internalRaw, applicationDate: app.applicationDate };
      const { line1, line2 } = getApplicationNumberSearchLines(numberFields);
      const financeClosed = Array.isArray(app.financialObligations)
        ? app.financialObligations.every((obl) => normalizedStatus(obl?.status) === 'PAID')
        : true;
      const paymentStatus = aggregateApplicationPaymentStatus(app.financialObligations);
      const cargo = (app as any).cargo;
      const cargoDelivered = Array.isArray(cargo) && cargo.length > 0
        ? cargo.every((item: any) => normalizedStatus(item?.status) === 'DELIVERED' || item?.deliveredStatus === 'full')
        : true;
      const completionProjection = (app as any).completionProjection ?? null;
      const searchBlob = [
        direction,
        line1,
        line2,
        internalLines.line1,
        internalLines.line2,
        clientLines.line1,
        clientLines.line2,
        String(app.id),
        app.number,
        app.externalNumber,
        app.internalNumber,
        clientName,
        contractLine,
        app.contract?.number,
        app.basisDate,
      ]
        .map((x) => normalize(x))
        .join(' ');

      return {
        id: app.id,
        status: app.status || '',
        direction,
        internalLine1: internalLines.line1,
        internalLine2: internalLines.line2,
        clientLine1: clientLines.line1,
        clientLine2: clientLines.line2,
        sortDate,
        clientName,
        contractLine,
        taskCount: taskCounts[app.id] ?? 0,
        paymentStatus,
        financeClosed,
        cargoDelivered,
        completionProjection,
        searchBlob,
      };
    });
  }, [applications, taskCounts]);

  const filteredSorted = useMemo(() => {
    const q = normalize(search);
    let list = baseRows.filter((row) => {
      if (q && !row.searchBlob.includes(q)) return false;
      if (completionView === 'terminal' && !Boolean(row.completionProjection?.physicalComplete)) return false;
      if (completionView === 'fully_closed' && !Boolean(row.completionProjection?.overallComplete)) return false;
      if (completionView === 'requires_closure' && !isRequiresClosure(row.completionProjection)) return false;
      if (completionView === 'active' && Boolean(row.completionProjection?.overallComplete)) return false;
      const { overdue } = workspaceHighlightForApplication(row.id, allActiveTasks);
      if (!matchesApplicationStatusFilter(row.status, statusFilter, overdue, row.completionProjection)) return false;
      if (scope === 'mine' && !mineApplicationIds.has(row.id)) return false;
      if (hasTasksFilter === 'with' && row.taskCount <= 0) return false;
      if (hasTasksFilter === 'without' && row.taskCount > 0) return false;
      return true;
    });

    const effectiveKey = sortKey ?? 'date';
    const effectiveDir = sortKey == null ? 'desc' : sortDir;

    list = [...list].sort((a, b) => {
      const aOverdue = workspaceHighlightForApplication(a.id, allActiveTasks).overdue;
      const bOverdue = workspaceHighlightForApplication(b.id, allActiveTasks).overdue;
      const priorityA = completionPriorityRank(a.completionProjection, aOverdue, a.taskCount);
      const priorityB = completionPriorityRank(b.completionProjection, bOverdue, b.taskCount);
      if (priorityA !== priorityB) return priorityA - priorityB;
      if (effectiveKey === 'date') {
        const d = a.sortDate - b.sortDate;
        if (d !== 0) return effectiveDir === 'asc' ? d : -d;
        return b.id - a.id;
      }
      if (effectiveKey === 'tasks') {
        const d = a.taskCount - b.taskCount;
        if (d !== 0) return effectiveDir === 'asc' ? d : -d;
        return b.id - a.id;
      }
      const c = String(a.status || '').localeCompare(String(b.status || ''), 'ru');
      if (c !== 0) return effectiveDir === 'asc' ? c : -c;
      const d = a.sortDate - b.sortDate;
      if (d !== 0) return effectiveDir === 'asc' ? d : -d;
      return b.id - a.id;
    });

    return list;
  }, [
    baseRows,
    search,
    statusFilter,
    scope,
    hasTasksFilter,
    completionView,
    sortKey,
    sortDir,
    allActiveTasks,
    mineApplicationIds,
  ]);

  const onHeaderSort = (key: WorkspaceSortKey) => {
    const next = toggleSort(key, sortKey, sortDir);
    setSortKey(next.key);
    setSortDir(next.dir);
  };

  const activeFilters = useMemo(() => {
    const items: string[] = [];
    if (search.trim()) items.push('Поиск');
    if (statusFilter === 'overdue') items.push('Просрочки');
    else if (statusFilter === 'in_work') items.push('В работе');
    else if (statusFilter === 'completed') items.push('Завершенные');
    if (scope === 'mine') items.push('Мои');
    if (hasTasksFilter === 'with') items.push('С задачами');
    if (hasTasksFilter === 'without') items.push('Без задач');
    if (completionView === 'terminal') items.push('Только физически завершенные');
    if (completionView === 'fully_closed') items.push('Только полностью закрытые');
    if (completionView === 'requires_closure') items.push('Требуют закрытия');
    if (completionView === 'active') items.push('Только активные');
    return items;
  }, [search, statusFilter, scope, hasTasksFilter, completionView]);

  const handleQuickFilter = (preset: 'problems' | 'overdue' | 'active' | 'without_tasks' | 'awaiting_docs') => {
    if (preset === 'problems') {
      setStatusFilter('overdue');
      setHasTasksFilter('with');
      return;
    }
    if (preset === 'overdue') {
      setStatusFilter('overdue');
      return;
    }
    if (preset === 'active') {
      setScope('mine');
      setStatusFilter('in_work');
      setHasTasksFilter('with');
      return;
    }
    if (preset === 'without_tasks') {
      setHasTasksFilter('without');
      setStatusFilter('all');
      return;
    }
    setSearch('документ');
    setStatusFilter('in_work');
    setHasTasksFilter('with');
  };

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setScope('all');
    setHasTasksFilter('all');
    setCompletionView('all');
  };

  const openApplicationDetails = (id: number) => {
    const preferDrawer =
      (roleKind === 'accountant' || roleKind === 'management') && typeof onOpenFinanceContext === 'function';
    if (preferDrawer) {
      onOpenFinanceContext!({ kind: 'application', id });
      return;
    }
    navigate(applicationCardPath(id, roleKind));
  };
  const firstInlineTaskForApplication = (applicationId: number): WorkspaceTask | null => {
    const candidates = allActiveTasks.filter((task) => task.applicationId === applicationId);
    if (!candidates.length) return null;
    const sorted = [...candidates].sort((a, b) => {
      const aOverdue = String(a.status || '').toUpperCase() === 'OVERDUE' ? 1 : 0;
      const bOverdue = String(b.status || '').toUpperCase() === 'OVERDUE' ? 1 : 0;
      if (aOverdue !== bOverdue) return bOverdue - aOverdue;
      const aDue = a.dueDate ? dayjs(a.dueDate).valueOf() : Number.POSITIVE_INFINITY;
      const bDue = b.dueDate ? dayjs(b.dueDate).valueOf() : Number.POSITIVE_INFINITY;
      if (aDue !== bDue) return aDue - bDue;
      return a.id - b.id;
    });
    return sorted[0] ?? null;
  };
  const attentionApplications = useMemo(
    () =>
      filteredSorted.filter((row) => {
        const overdue = workspaceHighlightForApplication(row.id, allActiveTasks).overdue;
        return isAttentionNeeded(row.completionProjection, overdue, row.taskCount);
      }).slice(0, 5),
    [filteredSorted, allActiveTasks]
  );

  if (!applications.length) {
    return (
      <Card
        title={listLayout.hideCardChrome ? undefined : 'Мои заявки'}
        bordered={!listLayout.hideCardChrome}
        style={listLayout.hideCardChrome ? listLayout.cardStyle : undefined}
      >
        <Text type="secondary">Заявок нет.</Text>
      </Card>
    );
  }

  return (
    <Card
      title={listLayout.hideCardChrome ? undefined : 'Все заявки'}
      bordered={!listLayout.hideCardChrome}
      style={listLayout.cardStyle}
      styles={{
        body: listLayout.cardBodyStyle,
      }}
    >
      <WorkspaceListToolbar
        searchPlaceholder="Внешний №, клиент, направление…"
        search={search}
        onSearch={setSearch}
        status={statusFilter}
        onStatus={setStatusFilter}
        scope={scope}
        onScope={setScope}
        hasTasks={hasTasksFilter}
        onHasTasks={setHasTasksFilter}
        onQuickFilter={handleQuickFilter}
        activeFilters={activeFilters}
        onResetFilters={resetFilters}
      />
      <div style={{ marginBottom: 8 }}>
        <Select
          size="small"
          value={completionView}
          onChange={(v) => setCompletionView(v)}
          style={{ minWidth: 260 }}
          options={[
            { value: 'all', label: 'Все заявки' },
            { value: 'terminal', label: 'Только терминальные' },
            { value: 'fully_closed', label: 'Только полностью закрытые' },
            { value: 'requires_closure', label: 'Требуют закрытия' },
            { value: 'active', label: 'Только активные' },
          ]}
        />
      </div>
      {attentionApplications.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary">Требуют внимания:</Text>{' '}
          <Space wrap size={6}>
            {attentionApplications.map((row) => {
              const overdue = workspaceHighlightForApplication(row.id, allActiveTasks).overdue;
              const label = overdue
                ? 'Просрочка/блокировка'
                : isRequiresClosure(row.completionProjection)
                  ? 'Требует закрытия'
                  : 'Требует внимания';
              return (
                <Tag key={`attn_app_${row.id}`} color={overdue ? 'red' : 'orange'}>
                  #{row.id}: {label}
                </Tag>
              );
            })}
          </Space>
        </div>
      )}
      <div style={listLayout.scrollAreaStyle}>
        <div style={{ ...WORKSPACE_APPLICATION_REGISTRY_HEADER_ROW, flexShrink: 0 }}>
          <WorkspaceHeaderCell sortKey="date" activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            № заявки
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Направление
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Клиент
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Заявка клиента
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey="tasks" activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Задачи
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Статус оплаты
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey="status" activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Статус
          </WorkspaceHeaderCell>
        </div>
        {!filteredSorted.length ? (
          <Empty description="Нет строк по фильтрам" style={{ margin: '24px 0' }} />
        ) : (
          filteredSorted.map((app) => {
            const highlight = workspaceHighlightForApplication(app.id, allActiveTasks);
            const rowStyle = workspaceRowHighlightStyle(highlight.overdue, highlight.today);

            return (
              <div
                key={app.id}
                role="presentation"
                onClick={() => openApplicationDetails(app.id)}
                onMouseEnter={() => setHoveredApplicationId(app.id)}
                onMouseLeave={() => setHoveredApplicationId(null)}
                style={{
                  ...rowStyle,
                  ...(hoveredApplicationId === app.id ? { background: '#f5f5f5' } : {}),
                  borderRadius: 8,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <div style={{ ...WORKSPACE_APPLICATION_REGISTRY_GRID_ROW, borderBottom: 'none', background: 'transparent' }}>
                  <div style={{ ...WORKSPACE_GRID_CELL, whiteSpace: 'normal', lineHeight: 1.35 }}>
                    <WorkspaceTwoLineTableCell line1={app.internalLine1} line2={app.internalLine2} />
                  </div>
                  <Text strong style={WORKSPACE_GRID_CELL} title={app.direction}>
                    {app.direction}
                  </Text>
                  <Text type="secondary" style={WORKSPACE_GRID_CELL} title={app.clientName || undefined}>
                    {app.clientName || '—'}
                  </Text>
                  <div style={{ ...WORKSPACE_GRID_CELL, whiteSpace: 'normal', lineHeight: 1.35 }}>
                    <WorkspaceTwoLineTableCell line1={app.clientLine1} line2={app.clientLine2} />
                  </div>
                  <div style={{ ...WORKSPACE_GRID_CELL, display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Text>{formatWorkspaceTasksCount(app.taskCount)}</Text>
                    {app.taskCount > 0 && onInlineTaskAction && (
                      <Button
                        size="small"
                        type="link"
                        style={{ paddingInline: 0 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const task = firstInlineTaskForApplication(app.id);
                          if (task) onInlineTaskAction(task);
                        }}
                      >
                        Выполнить
                      </Button>
                    )}
                    {app.completionProjection?.physicalComplete && (
                      <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                        Перевозка завершена
                      </Tag>
                    )}
                    {app.completionProjection?.physicalComplete && !app.completionProjection?.overallComplete && (
                      <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                        Есть незакрытые этапы
                      </Tag>
                    )}
                    {app.completionProjection?.overallComplete && (
                      <Tag color="green" style={{ marginInlineEnd: 0 }}>
                        Полностью закрыт
                      </Tag>
                    )}
                  </div>
                  <span
                    style={{
                      ...WORKSPACE_GRID_CELL,
                      display: 'flex',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <StatusCell status={app.paymentStatus} />
                  </span>
                  <span
                    style={{
                      ...WORKSPACE_GRID_CELL,
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <StatusCell status={app.status} />
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
};
